import React, { useMemo } from "react";
import { Box, Typography, Paper, styled, Tooltip as MuiTooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from "recharts";

// ==========================================
// 1. STYLED COMPONENTS & HELPERS
// ==========================================

const StyledTitle = styled(Typography)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  border-bottom: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledTitleRight = styled(StyledTitle)`
  flex: 1;
  text-align: center;
`;

const StyledTitleLeft = styled(StyledTitle)`
  flex: 2;
  border-right: 2px solid rgba(226, 230, 243, 0.8);
  padding: 0 !important;
`;

const StyledInfo = styled(StyledTitle)`
  color: #232d3d;
`;

const StyledInfoLeft = styled(StyledInfo)`
  border-right: 2px solid rgba(226, 230, 243, 0.8);
  flex: 2;
`;

const StyledInfoRight = styled(StyledInfo)`
  flex: 1;
  text-align: center;
`;

const StatusCell = ({ status }) => {
  const isPass = status.toLowerCase() === "pass";
  return (
    <Box
      sx={{
        bgcolor: isPass ? "#e8f5e9" : "#ffebee",
        color: isPass ? "#2e7d32" : "#d32f2f",
        p: 1,
        textAlign: "center",
        fontWeight: 600,
        fontSize: "12px",
        textTransform: "uppercase",
        flex: 1,
        border: "1px solid #f0f0f0",
        borderRadius: 1,
        mx: 0.5,
      }}
    >
      {status}
    </Box>
  );
};

const transformData = (patterns) => {
  const STATUS_COLORS = {
    "pass-pass-pass": "#10B981",
    "pass-pass-fail": "#6366F1",
    "pass-fail-pass": "#8B5CF6",
    "pass-fail-fail": "#F59E0B",
    "fail-pass-pass": "#60A5FA",
    "fail-pass-fail": "#F97316",
    "fail-fail-pass": "#EC4899",
    "fail-fail-fail": "#EF4444",
  };

  const COLORS = [
    "#00C49F",
    "#0088FE",
    "#FFBB28",
    "#FF8042",
    "#a855f7",
    "#FF6B6B",
    "#52b788",
    "#845EC2",
  ];

  const customOrder = [
    "pass-pass-pass",
    "pass-pass-fail",
    "pass-fail-fail",
    "pass-fail-pass",
    "fail-pass-pass",
    "fail-pass-fail",
    "fail-fail-pass",
    "fail-fail-fail",
  ];

  const dataArray = Object.entries(patterns)
    .filter(([name, value]) => value > 0)
    .map(([name, value]) => ({
      name,
      value,
      statuses: name.split("-"),
      color: STATUS_COLORS[name] || COLORS[Math.floor(Math.random() * COLORS.length)],
    }));

  return dataArray.sort((a, b) => {
    const indexA = customOrder.indexOf(a.name);
    const indexB = customOrder.indexOf(b.name);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

// Empty state component
const EmptyState = ({ message = "No execution patterns found" }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
      px: 3,
      bgcolor: "#f8f9fa",
      borderRadius: 2,
      border: "1px dashed #d0d0d0",
    }}
  >
    <Typography
      sx={{
        color: "#666",
        fontSize: "16px",
        fontWeight: 500,
        mb: 1,
      }}
    >
      {message}
    </Typography>
  </Box>
);

function usePatternData(patterns) {
  const data = useMemo(() => transformData(patterns), [patterns]);
  const total = useMemo(
    () => Object.values(patterns).reduce((sum, value) => sum + value, 0),
    [patterns],
  );

  return { data, total };
}

// ==========================================
// 2. PATTERN DETAILS TABLE COMPONENT
// ==========================================
export function PatternDetailsTable({ patterns }) {
  const { data, total } = usePatternData(patterns);

  if (data.length === 0 || total === 0) {
    return (
      <Box
        flex={1}
        overflow="hidden"
        sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
      >
        <Box display="flex" bgcolor={"#2550D1"}>
          <StyledTitleLeft sx={{ textAlign: "center" }}>
            Execution patterns across last 3 runs
            <Box
              sx={{
                display: "flex",
                mt: 1,
                borderTop: "2px solid rgba(226, 230, 243, 0.8)",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: "12px",
                  borderRight: "2px solid rgba(226, 230, 243, 0.8)",
                }}
              >
                Latest Run
              </Box>
              <Box
                sx={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: "12px",
                  borderRight: "2px solid rgba(226, 230, 243, 0.8)",
                }}
              >
                Previous Run
              </Box>
              <Box
                sx={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: "12px",
                }}
              >
                Two Runs Ago
              </Box>
            </Box>
          </StyledTitleLeft>
          <StyledTitleRight>Test Case Count (%)</StyledTitleRight>
        </Box>
        <Box bgcolor={"#ffffff"} p={3}>
          <EmptyState message="No execution patterns available" />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      flex={1}
      overflow="hidden"
      sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
    >
      <Box display="flex" bgcolor={"#2550D1"}>
        <StyledTitleLeft sx={{ textAlign: "center" }}>
          Execution patterns across last 3 runs
          <Box
            sx={{
              display: "flex",
              mt: 1,
              borderTop: "2px solid rgba(226, 230, 243, 0.8)",
            }}
          >
            <Box
              sx={{
                flex: 1,
                textAlign: "center",
                fontSize: "12px",
                borderRight: "2px solid rgba(226, 230, 243, 0.8)",
              }}
            >
              Latest Run
            </Box>
            <Box
              sx={{
                flex: 1,
                textAlign: "center",
                fontSize: "12px",
                borderRight: "2px solid rgba(226, 230, 243, 0.8)",
              }}
            >
              Previous Run
            </Box>
            <Box
              sx={{
                flex: 1,
                textAlign: "center",
                fontSize: "12px",
              }}
            >
              Two Runs Ago
            </Box>
          </Box>
        </StyledTitleLeft>
        <StyledTitleRight>Test Case Count (%)</StyledTitleRight>
      </Box>
      {data.map((item, index) => (
        <Box key={index} display="flex" bgcolor={"#ffffff"}>
          <StyledInfoLeft>
            <Box sx={{ display: "flex", mt: 1 }}>
              <StatusCell status={item.statuses[0]} />
              <StatusCell status={item.statuses[1]} />
              <StatusCell status={item.statuses[2]} />
            </Box>
          </StyledInfoLeft>
          <StyledInfoRight>
            {item.value} {total > 0 ? `(${((item.value / total) * 100).toFixed(1)}%)` : "(0%)"}
          </StyledInfoRight>
        </Box>
      ))}
    </Box>
  );
}

// ==========================================
// 3. BAR GRAPH COMPONENT
// ==========================================
export function PatternBarGraph({ patterns }) {
  const { data, total } = usePatternData(patterns);

  const TooltipContent = ({ item }) => (
    <Paper sx={{ p: 2, boxShadow: 3, width: "max-content", maxWidth: "600px" }}>
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ mb: 1, color: "#333", display: "block" }}
      >
        Test cases with this pattern:
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
          p: 1,
          bgcolor: "#f8f9fa",
          borderRadius: 1,
        }}
      >
        {/* Latest */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#666", fontSize: "11px" }}>
            Latest:
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: 0.5,
              bgcolor: item.statuses[0] === "pass" ? "#e8f5e9" : "#ffebee",
              color: item.statuses[0] === "pass" ? "#2e7d32" : "#d32f2f",
              fontWeight: 600,
              fontSize: "11px",
            }}
          >
            {item.statuses[0].toUpperCase()}
          </Box>
        </Box>

        <Typography sx={{ color: "#999", fontWeight: 600, fontSize: "12px" }}>→</Typography>

        {/* Previous */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#666", fontSize: "11px" }}>
            Previous:
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: 0.5,
              bgcolor: item.statuses[1] === "pass" ? "#e8f5e9" : "#ffebee",
              color: item.statuses[1] === "pass" ? "#2e7d32" : "#d32f2f",
              fontWeight: 600,
              fontSize: "11px",
            }}
          >
            {item.statuses[1].toUpperCase()}
          </Box>
        </Box>

        <Typography sx={{ color: "#999", fontWeight: 600, fontSize: "12px" }}>→</Typography>

        {/* 2 Runs Ago */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#666", fontSize: "11px" }}>
            2 Runs Ago:
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: 0.5,
              bgcolor: item.statuses[2] === "pass" ? "#e8f5e9" : "#ffebee",
              color: item.statuses[2] === "pass" ? "#2e7d32" : "#d32f2f",
              fontWeight: 600,
              fontSize: "11px",
            }}
          >
            {item.statuses[2].toUpperCase()}
          </Box>
        </Box>
      </Box>

      {/* Count display */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Typography variant="body2" sx={{ color: "#666" }}>
          <span style={{ fontWeight: 600, color: "#333" }}>{item.value}</span> test cases
        </Typography>
      </Box>
    </Paper>
  );

  const CustomBar = (props) => {
    const { fill, x, y, width, height, payload } = props;

    return (
      <MuiTooltip
        title={<TooltipContent item={payload} />}
        placement="right"
        arrow
        enterDelay={0}
        leaveDelay={200}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: "none",
              backgroundColor: "transparent",
              p: 0,
            },
          },
        }}
      >
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          rx={4}
          ry={4}
          style={{ cursor: "pointer" }}
        />
      </MuiTooltip>
    );
  };

  if (data.length === 0 || total === 0) {
    return (
      <Paper sx={{ p: 3, height: "100%" }}>
        <Box sx={{ height: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <EmptyState message="No execution patterns to display" />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              color: "#232D3D",
              fontSize: "16px",
              fontWeight: 600,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            Test Count Grouped by Execution Pattern Across the Last 3 Runs
            <MuiTooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    What this graph shows
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Groups test cases based on how their results changed across the last three
                    executions.
                  </Typography>
                  <Typography variant="body2">
                    Example: <strong>pass–fail–pass</strong> indicates a flaky test.
                  </Typography>
                </Box>
              }
              arrow
              placement="right"
            >
              <IconButton size="small" sx={{ color: "#666" }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: 500 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 70 }} barSize={40}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 12 }}
              interval={0}
              tickFormatter={(value) => value.toUpperCase()}
            />
            <YAxis />
            <Bar dataKey="value" name="Count" shape={<CustomBar />}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
